import { HIDDEN_ATTRIBUTES } from "../../api/config"
import type { Attribute, ProductGroup, ProductRow } from "../../api/types"

/** Колонка табличной части — атрибут из `attributesColumnOrder` группы,
 *  дополненный названием из справочника атрибутов. */
export interface Column {
	id: number
	name: string
	sort: number
	isImportant: boolean
	importantSort: number | null
	isMain: boolean
}

/** Колонки карточки в порядке показа. Служебные атрибуты (сортировка,
 *  «не выводить», вес) в таблице не участвуют. */
export function columnsOf(group: ProductGroup, attributes?: Attribute[]): Column[] {
	const names = new Map(attributes?.map(item => [item.id, item.name]))
	return Object.entries(group.attributesColumnOrder ?? {})
		.map(([key, descr]) => ({ id: Number(key), ...descr }))
		.filter(column => !HIDDEN_ATTRIBUTES.includes(column.id))
		.sort((a, b) => a.sort - b.sort)
		.map(column => ({
			...column,
			name: names.get(column.id) ?? `атрибут ${column.id}`,
			isMain: group.mainAttributeId === column.id,
		}))
}

/** Атрибуты, которых в карточке ещё нет, — из них набирают новые колонки. */
export function availableAttributes(
	group: ProductGroup,
	attributes?: Attribute[]
): Attribute[] {
	const order = group.attributesColumnOrder ?? {}
	return (attributes ?? [])
		.filter(
			item =>
				item.id > 0 &&
				!(item.id in order) &&
				!HIDDEN_ATTRIBUTES.includes(item.id)
		)
		.sort((a, b) => a.name.localeCompare(b.name))
}

/** Значение атрибута у товара — как оно лежит в ответе; пустая строка,
 *  если атрибута у товара нет. Не обрезаем: с этим значением сравнивается
 *  черновик ячейки, и лишний пробел не должен делать строку «изменённой». */
export function valueOf(row: ProductRow, attributeId: number): string {
	return row.attributeValues.find(item => item.id === attributeId)?.value ?? ""
}

/**
 * Клиентское превью короткого имени товара для сайта — тот же алгоритм, что
 * `ProductNameGenerator.cs:44-95`: название группы → значения важных
 * атрибутов по приоритету (имя, значение) → торговая марка → артикул.
 *
 * Это именно превью, не настоящее значение. Настоящее лежит в базе
 * (`Products.GeneratedNameJson`) и ни один доступный Риге метод `ecm` его не
 * отдаёт; единицу измерения атрибута и название торговой марки на клиенте
 * тоже взять неоткуда, поэтому марка показана плейсхолдером. Пересобрать
 * настоящее значение может только кнопка «Сгенерировать короткие имена по
 * всей базе» (`SiteNameHelp.tsx`) — но её результат обратно в интерфейс не
 * возвращается.
 *
 * `null` — ERP не соберёт имя вовсе: у товара пусто хотя бы в одном важном
 * атрибуте (`ProductNameGenerator.cs:64-77`), либо у карточки нет названия.
 */
export function previewSiteName(
	groupName: string,
	important: Column[],
	row: ProductRow,
	hasSellmark: boolean
): string | null {
	const name = groupName.trim()
	if (!name) return null
	if (important.some(column => valueOf(row, column.id).trim() === "")) return null

	const attrs = important
		.map(column => {
			const value = valueOf(row, column.id).trim()
			return value ? `${column.name} ${value}` : null
		})
		.filter(Boolean)
		.join(", ")

	return [name, attrs, hasSellmark ? "(торговая марка)" : "", row.id]
		.filter(Boolean)
		.join(" ")
}
